import { useCallback, useMemo, useState } from 'react';

import { sanitizeNote } from '../cart';

export interface UseNoteInputArgs {
  id: string;
  name: string;
  defaultValue: string;
  maxLength: number;
  /**
   * Optional side-effect callback invoked as the user edits the note.
   *
   * In the cart note editor we intentionally do NOT submit on every keystroke;
   * we only submit when the user confirms (green check). In that case, this
   * callback should be omitted and the caller should submit explicitly.
   */
  onChangeNote?: (note: string) => void;
}

export interface UseNoteInputReturn {
  currentNote: string;
  setCurrentNote: (note: string) => void;
  inputProps: {
    id: string;
    name: string;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onInput: (e: React.FormEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    maxLength: number;
  };
  charCount: number;
}

export const useNoteInput = (args: UseNoteInputArgs): UseNoteInputReturn => {
  const { id, name, defaultValue, maxLength, onChangeNote } = args;

  const [noteValue, setNoteValue] = useState<string>(defaultValue);

  const setCurrentNote = useCallback((note: string) => {
    setNoteValue(sanitizeNote(note));
  }, []);

  const handleInput = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    const sanitized = sanitizeNote((e.currentTarget as HTMLInputElement).value);
    (e.currentTarget as HTMLInputElement).value = sanitized;
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeNote(e.target.value);
      setNoteValue(sanitized);
      onChangeNote?.(sanitized);
    },
    [onChangeNote]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const trimmed = sanitizeNote(e.currentTarget.value).trim();
      e.currentTarget.value = trimmed;
      setNoteValue(trimmed);
      onChangeNote?.(trimmed);
    },
    [onChangeNote]
  );

  const inputProps = useMemo(
    () => ({
      id,
      name,
      value: noteValue,
      onChange: handleChange,
      onInput: handleInput,
      onBlur: handleBlur,
      maxLength,
    }),
    [id, name, noteValue, handleChange, handleInput, handleBlur, maxLength]
  );

  const charCount = useMemo(() => noteValue.length, [noteValue]);

  return {
    currentNote: noteValue,
    setCurrentNote,
    inputProps,
    charCount,
  };
};
